import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  X, 
  ArrowRight, 
  Clock, 
  TrendingUp, 
  Package, 
  BookOpen, 
  FileText,
  Command
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../hooks/useApi';
import { cn } from '../utils/cn';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Use the search hook
  const { results, loading: isLoading } = useSearch(query);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load suggestions when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowSuggestions(true);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const totalItems = showSuggestions ? 0 : results.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [selectedIndex, results, showSuggestions, onClose]);

  // Handle result click
  const handleResultClick = (result: any) => {
    // Navigate based on result type
    if (result.type === 'product') {
      navigate(`/products`);
    } else if (result.type === 'tutorial') {
      navigate(`/tutorials/${result.id}`);
    }
    onClose();
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
  };

  // Get result icon
  const getResultIcon = (type: string) => {
    switch (type) {
      case 'product':
        return Package;
      case 'tutorial':
        return BookOpen;
      case 'doc':
        return FileText;
      default:
        return Search;
    }
  };

  // Get result type label
  const getResultTypeLabel = (type: string) => {
    switch (type) {
      case 'product':
        return 'Product';
      case 'tutorial':
        return 'Tutorial';
      case 'doc':
        return 'Documentation';
      default:
        return 'Result';
    }
  };

  // Render search results
  const renderResults = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#667eea]"></div>
        </div>
      );
    }

    if (results.length === 0 && query.trim()) {
      return (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-600 mb-4">Try searching for something else</p>
          <div className="text-sm text-gray-500">
            <p className="mb-2">Popular searches:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['BOMG Overview', 'Product Master', 'Pricing Engine', 'Getting Started'].map((term) => (
                <button
                  key={term}
                  onClick={() => setQuery(term)}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Group results by type
    const groupedResults = results.reduce((acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    }, {} as Record<string, typeof results>);

    return (
      <div className="space-y-6">
        {Object.entries(groupedResults).map(([type, typeResults]) => (
          <div key={type}>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
              {getResultTypeLabel(type)} ({(typeResults as any[]).length})
            </h3>
            <div className="space-y-2">
              {(typeResults as any[]).map((result: any, index: number) => {
                const globalIndex = results.indexOf(result);
                const Icon = getResultIcon(result.type);
                const isSelected = selectedIndex === globalIndex;
                
                return (
                  <motion.button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={cn(
                      "w-full flex items-start space-x-3 p-4 rounded-lg text-left transition-colors",
                      isSelected
                        ? "bg-blue-50 border border-blue-200"
                        : "hover:bg-gray-50"
                    )}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center",
                        result.type === 'product' ? "bg-blue-100" :
                        result.type === 'tutorial' ? "bg-green-100" :
                        "bg-purple-100"
                      )}>
                        <Icon className={cn(
                          "w-4 h-4",
                          result.type === 'product' ? "text-blue-600" :
                          result.type === 'tutorial' ? "text-green-600" :
                          "text-purple-600"
                        )} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 mb-1">{result.title}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2">{result.description}</p>
                      {result.category && (
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                          {result.category}
                        </span>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render suggestions
  const renderSuggestions = () => {
    const recentSearches: string[] = [];
    const popularSearches = ['BOMG Overview', 'Product Master', 'Pricing Engine', 'Getting Started'];
    
    return (
      <div className="space-y-6">
        {recentSearches.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Recent Searches
            </h3>
            <div className="space-y-1">
              {recentSearches.slice(0, 5).map((term, index) => (
                <button
                  key={term}
                  onClick={() => handleSuggestionClick(term)}
                  className={cn(
                    "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors",
                    selectedIndex === index
                      ? "bg-blue-50 border border-blue-200"
                      : "hover:bg-gray-50"
                  )}
                >
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{term}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Popular Searches
          </h3>
          <div className="space-y-1">
            {popularSearches.slice(0, 5).map((term, index) => {
              const globalIndex = recentSearches.length + index;
              return (
                <button
                  key={term}
                  onClick={() => handleSuggestionClick(term)}
                  className={cn(
                    "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors",
                    selectedIndex === globalIndex
                      ? "bg-blue-50 border border-blue-200"
                      : "hover:bg-gray-50"
                  )}
                >
                  <TrendingUp className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{term}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        {/* Modal */}
        <motion.div
          className="relative bg-white h-full flex flex-col"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search products, tutorials, and documentation..."
                  className="w-full pl-10 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                />
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Keyboard shortcuts hint */}
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">↑↓</kbd>
                  <span>Navigate</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Enter</kbd>
                  <span>Select</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Esc</kbd>
                  <span>Close</span>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Command className="w-4 h-4" />
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">K</kbd>
                <span>to open</span>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto">
              {showSuggestions ? renderSuggestions() : renderResults()}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SearchModal;
